(() => {
  'use strict';
  if (window.QXCommerce?.version) return;

  const ROOT = window.Shopify?.routes?.root || '/';
  const CART_SECTION = 'qx-cart-drawer';
  const WKEY = 'qxWishlistProducts';
  const $ = (selector, scope = document) => scope?.querySelector?.(selector) || null;
  const $$ = (selector, scope = document) => scope?.querySelectorAll ? [...scope.querySelectorAll(selector)] : [];
  const clampQty = value => Math.max(1, Math.min(99, Number.parseInt(value, 10) || 1));
  const esc = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

  let memoryWishlist = [];
  let cartQueue = Promise.resolve();
  let wishlistOpener = null;
  let cartOpener = null;
  let observerQueued = false;

  const announce = message => {
    const node = $('[data-qx-commerce-live]') || $('[data-qx-global-live]') || $('[data-qx-wishlist-live]');
    if (!node || !message) return;
    node.textContent = '';
    requestAnimationFrame(() => { node.textContent = message; });
  };

  const toast = (message, error = false) => {
    if (!message) return;
    let node = $('[data-qx-ux-toast]');
    if (!node) {
      node = document.createElement('div');
      node.className = 'qx-ux-toast';
      node.dataset.qxUxToast = '';
      document.body.appendChild(node);
    }
    node.setAttribute('role', error ? 'alert' : 'status');
    node.setAttribute('aria-live', error ? 'assertive' : 'polite');
    node.textContent = message;
    node.classList.add('is-visible');
    clearTimeout(node._qxCommerceTimer);
    node._qxCommerceTimer = setTimeout(() => node.classList.remove('is-visible'), 2200);
    announce(message);
  };

  const normalize = raw => {
    if (!raw || typeof raw !== 'object' || !raw.handle) return null;
    return {
      handle: String(raw.handle),
      title: String(raw.title || ''),
      url: String(raw.url || ''),
      image: String(raw.image || ''),
      price: String(raw.price || ''),
      vendor: String(raw.vendor || ''),
      variantId: raw.variantId ? String(raw.variantId) : '',
      variantTitle: String(raw.variantTitle || ''),
      available: raw.available !== false,
      quantity: clampQty(raw.quantity || 1)
    };
  };

  const keyOf = item => `${item?.handle || ''}::${item?.variantId || 'product'}`;

  const normalizeList = source => {
    const out = [];
    const seen = new Set();
    (Array.isArray(source) ? source : []).forEach(raw => {
      const item = normalize(raw);
      if (!item) return;
      const key = keyOf(item);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(item);
    });
    return out.slice(0, 60);
  };

  const readWishlist = () => {
    try {
      memoryWishlist = normalizeList(JSON.parse(localStorage.getItem(WKEY) || '[]'));
    } catch (_) {}
    return [...memoryWishlist];
  };

  const writeWishlist = items => {
    const next = normalizeList(items);
    memoryWishlist = next;
    let persisted = false;
    try {
      localStorage.setItem(WKEY, JSON.stringify(next));
      persisted = true;
    } catch (_) {}
    window.dispatchEvent(new CustomEvent('qx:wishlist-change', { detail: { items: next, persisted } }));
    return { items: next, persisted };
  };

  const itemFromButton = button => {
    const host = button.closest('[data-qx-product-card],[data-product-root],[data-qv-dialog]') || button;
    return normalize({
      handle: button.dataset.qxProductHandle || host.dataset.qxProductHandle || '',
      title: button.dataset.qxProductTitle || host.dataset.qxProductTitle || '',
      url: button.dataset.qxProductUrl || host.dataset.qxProductUrl || '',
      image: button.dataset.qxProductImage || host.dataset.qxProductImage || '',
      price: button.dataset.qxProductPrice || host.dataset.qxProductPrice || '',
      vendor: button.dataset.qxProductVendor || host.dataset.qxProductVendor || '',
      variantId: button.dataset.qxProductVariantId || host.dataset.qxProductVariantId || '',
      variantTitle: button.dataset.qxProductVariantTitle || host.dataset.qxProductVariantTitle || '',
      available: (button.dataset.qxProductAvailable || host.dataset.qxSelectedVariantAvailable || host.dataset.qxProductAvailable) !== 'false'
    });
  };

  const isSaved = (item, list = readWishlist()) => {
    if (!item?.handle) return false;
    if (item.variantId) return list.some(saved => keyOf(saved) === keyOf(item) || (saved.handle === item.handle && !saved.variantId));
    return list.some(saved => saved.handle === item.handle);
  };

  const syncWishlistUi = () => {
    const items = readWishlist();
    $$('[data-qx-wishlist-count]').forEach(node => {
      node.textContent = node.closest('[data-qx-wishlist-page]')
        ? (items.length ? `${items.length} saved product${items.length === 1 ? '' : 's'}` : '')
        : String(items.length);
    });
    $$('[data-qx-wishlist-toggle]').forEach(button => {
      const active = isSaved(itemFromButton(button), items);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = $('[data-qx-wishlist-label]', button);
      if (label) label.textContent = active ? 'Saved' : 'Save to wishlist';
    });
  };

  const productTitleMarkup = item => item.url
    ? `<a href="${esc(item.url)}">${esc(item.title || 'Product')}</a>`
    : `<strong class="qx-wishlist-drawer__title">${esc(item.title || 'Product')}</strong>`;

  const wishlistRow = item => {
    const key = keyOf(item);
    const row = document.createElement('article');
    row.className = 'qx-wishlist-drawer__item';
    row.dataset.qxWishlistKey = key;
    row.innerHTML = `${item.image ? `<img src="${esc(item.image)}" alt="" loading="lazy">` : '<div class="qx-wishlist-drawer__placeholder"></div>'}
      <div>${productTitleMarkup(item)}
      <div class="qx-wishlist-drawer__row-meta">${item.variantTitle && item.variantTitle !== 'Default Title' ? `<span>${esc(item.variantTitle)}</span>` : ''}${item.price ? `<span>${esc(item.price)}</span>` : ''}<span>${item.available === false ? 'Unavailable' : 'Available'}</span></div>
      <div class="qx-wishlist-drawer__qty"><button type="button" data-qx-commerce-wish-minus aria-label="Decrease quantity">−</button><input type="number" min="1" max="99" value="${item.quantity}" inputmode="numeric" data-qx-commerce-wish-qty aria-label="Quantity"><button type="button" data-qx-commerce-wish-plus aria-label="Increase quantity">+</button></div>
      <div class="qx-wishlist-drawer__actions"><button type="button" class="qx-wishlist-drawer__move" data-qx-commerce-wishlist-move="${esc(key)}" ${item.available === false || !item.variantId ? 'disabled' : ''}>Move to cart</button>${item.url ? `<a class="qx-wishlist-drawer__change" href="${esc(item.url)}">Change variant</a>` : ''}</div></div>
      <button type="button" class="qx-wishlist-drawer__remove" data-qx-commerce-wishlist-remove="${esc(key)}" aria-label="Remove ${esc(item.title || 'product')}">×</button>`;
    return row;
  };

  const renderWishlistDrawer = () => {
    const drawer = $('[data-qx-wishlist-drawer]');
    if (!drawer) return;
    const items = readWishlist();
    const list = $('[data-qx-wishlist-drawer-items]', drawer);
    const empty = $('[data-qx-wishlist-drawer-empty]', drawer);
    const count = $('[data-qx-wishlist-drawer-count]', drawer);
    if (count) count.textContent = items.length ? `${items.length} saved product${items.length === 1 ? '' : 's'}` : '';
    if (empty) empty.hidden = items.length > 0;
    if (list) list.replaceChildren(...items.map(wishlistRow));
  };

  const renderWishlistPage = () => {
    $$('[data-qx-wishlist-page]').forEach(section => {
      const items = readWishlist();
      const grid = $('[data-qx-wishlist-grid]', section);
      const empty = $('[data-qx-wishlist-empty]', section);
      const clear = $('[data-qx-wishlist-clear]', section);
      const count = $('[data-qx-wishlist-count]', section);
      if (count) count.textContent = items.length ? `${items.length} saved product${items.length === 1 ? '' : 's'}` : '';
      if (empty) empty.hidden = items.length > 0;
      if (clear) clear.hidden = items.length === 0;
      if (!grid) return;
      grid.innerHTML = '';
      items.forEach(item => {
        const key = keyOf(item);
        const card = document.createElement('article');
        card.className = 'qx-wishlist__card';
        card.dataset.qxWishlistKey = key;
        const media = item.url
          ? `<a class="qx-wishlist__media" href="${esc(item.url)}">${item.image ? `<img src="${esc(item.image)}" alt="${esc(item.title)}" loading="lazy">` : ''}</a>`
          : `<div class="qx-wishlist__media">${item.image ? `<img src="${esc(item.image)}" alt="${esc(item.title)}" loading="lazy">` : ''}</div>`;
        const title = item.url ? `<a href="${esc(item.url)}">${esc(item.title)}</a>` : `<strong>${esc(item.title)}</strong>`;
        const options = item.variantId && item.available !== false
          ? `<button type="button" data-qx-commerce-wishlist-move="${esc(key)}">Move to cart</button>`
          : (item.url ? `<a href="${esc(item.url)}">Choose options</a>` : '');
        card.innerHTML = `${media}<div class="qx-wishlist__body">${item.vendor ? `<small>${esc(item.vendor)}</small>` : ''}${title}${item.variantTitle && item.variantTitle !== 'Default Title' ? `<small>${esc(item.variantTitle)}</small>` : ''}<strong>${esc(item.price)}</strong><div class="qx-wishlist__actions">${options}<button type="button" data-qx-commerce-wishlist-remove="${esc(key)}">Remove</button></div></div>`;
        grid.appendChild(card);
      });
    });
  };

  const focusables = box => $$('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', box)
    .filter(element => !element.hidden && element.getClientRects().length);

  const openWishlist = opener => {
    const drawer = $('[data-qx-wishlist-drawer]');
    if (!drawer) return;
    wishlistOpener = opener instanceof HTMLElement ? opener : document.activeElement;
    renderWishlistDrawer();
    drawer.hidden = false;
    document.body.classList.add('qx-wishlist-drawer-open');
    document.documentElement.classList.add('qx-modal-open');
    requestAnimationFrame(() => $('.qx-wishlist-drawer__close', drawer)?.focus());
  };

  const closeWishlist = () => {
    const drawer = $('[data-qx-wishlist-drawer]');
    if (!drawer || drawer.hidden) return;
    drawer.hidden = true;
    document.body.classList.remove('qx-wishlist-drawer-open');
    document.documentElement.classList.remove('qx-modal-open');
    const opener = wishlistOpener;
    wishlistOpener = null;
    if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
  };

  const toggleWishlist = button => {
    const item = itemFromButton(button);
    if (!item?.handle) return;
    const items = readWishlist();
    const exact = keyOf(item);
    const exactIndex = items.findIndex(saved => keyOf(saved) === exact);
    const legacyIndex = item.variantId ? items.findIndex(saved => saved.handle === item.handle && !saved.variantId) : -1;
    const index = exactIndex >= 0 ? exactIndex : legacyIndex;
    if (index >= 0) {
      writeWishlist(items.filter((_, i) => i !== index));
      toast('Removed from wishlist');
    } else {
      const result = writeWishlist([item, ...items.filter(saved => keyOf(saved) !== exact)]);
      toast(result.persisted ? 'Saved to wishlist' : 'Saved for this tab only');
      openWishlist(button);
    }
    syncWishlistUi();
    renderWishlistDrawer();
    renderWishlistPage();
  };

  const publishCart = extra => {
    const count = Number($('[data-qx-cart-shell]')?.dataset.cartCount || 0);
    $$('.qx-cart-count,[data-qx-cart-count]').forEach(node => { node.textContent = String(count); });
    window.dispatchEvent(new CustomEvent('qx:cart:updated', { detail: { itemCount: count, ...(extra || {}) } }));
    return count;
  };

  const replaceCartSection = (html, shouldOpen = false) => {
    if (!html) return false;
    const current = document.getElementById(`shopify-section-${CART_SECTION}`);
    if (!current) return false;
    const wasOpen = shouldOpen || Boolean($('[data-qx-cart-shell].is-open', current));
    const temp = document.createElement('div');
    temp.innerHTML = html.trim();
    const incoming = temp.querySelector(`#shopify-section-${CART_SECTION}`);
    if (incoming) current.replaceWith(incoming);
    else current.innerHTML = html;
    const shell = $('[data-qx-cart-shell]');
    if (wasOpen && shell) {
      shell.classList.add('is-open');
      document.body.classList.add('qx-cart-open');
    }
    publishCart({ source: 'section-render' });
    return true;
  };

  const refreshCart = async ({ section = false, open = false } = {}) => {
    if (section) {
      const response = await fetch(`${ROOT}?sections=${encodeURIComponent(CART_SECTION)}`, { headers: { Accept: 'application/json' } });
      const data = await response.json();
      replaceCartSection(data?.[CART_SECTION], open);
    }
    const response = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    $$('.qx-cart-count,[data-qx-cart-count]').forEach(node => { node.textContent = String(cart.item_count || 0); });
    window.dispatchEvent(new CustomEvent('qx:cart:updated', { detail: { itemCount: Number(cart.item_count || 0), cart, source: 'refresh' } }));
    return cart;
  };

  const rawCartRequest = async (endpoint, payload = {}, options = {}) => {
    const request = { method: 'POST', headers: { Accept: 'application/json' } };
    if (payload instanceof FormData) {
      payload.set('sections', CART_SECTION);
      payload.set('sections_url', location.pathname + location.search);
      request.body = payload;
    } else {
      request.headers['Content-Type'] = 'application/json';
      request.body = JSON.stringify({ ...payload, sections: CART_SECTION, sections_url: location.pathname + location.search });
    }
    const response = await fetch(ROOT + endpoint, request);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Cart update failed');
    const replaced = replaceCartSection(data.sections?.[CART_SECTION], options.open !== false);
    if (!replaced) await refreshCart({ section: false });
    return data;
  };

  const cartRequest = (endpoint, payload = {}, options = {}) => {
    const run = () => rawCartRequest(endpoint, payload, options);
    const next = cartQueue.then(run, run);
    cartQueue = next.catch(() => {});
    return next;
  };

  const addItems = (items, open = true) => {
    const clean = (items || [])
      .map(item => ({ id: Number(item.id), quantity: clampQty(item.quantity || 1) }))
      .filter(item => item.id);
    if (!clean.length) return Promise.reject(new Error('Choose an available product'));
    return cartRequest('cart/add.js', { items: clean }, { open });
  };

  const openCart = opener => {
    const shell = $('[data-qx-cart-shell]');
    if (!shell) {
      location.href = ROOT + 'cart';
      return;
    }
    cartOpener = opener instanceof HTMLElement ? opener : document.activeElement;
    shell.classList.add('is-open');
    document.body.classList.add('qx-cart-open');
    requestAnimationFrame(() => $('.qx-cart-drawer', shell)?.focus());
  };

  const closeCart = () => {
    const shell = $('[data-qx-cart-shell]');
    if (!shell) return;
    shell.classList.remove('is-open');
    document.body.classList.remove('qx-cart-open');
    const opener = cartOpener;
    cartOpener = null;
    if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
  };

  const moveWishlistItem = async (key, trigger) => {
    const items = readWishlist();
    const item = items.find(saved => keyOf(saved) === key);
    if (!item?.variantId || item.available === false) {
      toast('Choose an available variant first', true);
      return;
    }
    const row = trigger?.closest('[data-qx-wishlist-key]');
    const quantity = clampQty($('[data-qx-commerce-wish-qty]', row)?.value || item.quantity || 1);
    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');
    try {
      await addItems([{ id: item.variantId, quantity }], true);
      writeWishlist(items.filter(saved => keyOf(saved) !== key));
      syncWishlistUi();
      renderWishlistDrawer();
      renderWishlistPage();
      toast('Moved to cart');
      openCart(trigger);
    } catch (error) {
      toast(error.message || 'Could not move item to cart', true);
    } finally {
      if (document.contains(trigger)) {
        trigger.disabled = false;
        trigger.removeAttribute('aria-busy');
      }
    }
  };

  const setSyncState = (status, state) => {
    if (!status) return;
    const labels = { local: 'localLabel', syncing: 'syncingLabel', synced: 'syncedLabel', unavailable: 'unavailableLabel', error: 'errorLabel' };
    status.dataset.state = state;
    status.textContent = status.dataset[labels[state]] || state;
  };

  const syncWishlistRemote = async trigger => {
    const url = trigger.dataset.syncUrl || '';
    const status = $('[data-qx-wishlist-sync-status]');
    if (!url) {
      setSyncState(status, 'unavailable');
      return;
    }
    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');
    setSyncState(status, 'syncing');
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ action: 'sync', timestamp: new Date().toISOString(), items: readWishlist().map(({ handle, variantId }) => ({ handle, variantId })) })
      });
      if (!response.ok) throw new Error('Wishlist sync service unavailable');
      setSyncState(status, 'synced');
      toast('Wishlist synced');
    } catch (error) {
      setSyncState(status, 'error');
      toast(error.message || 'Wishlist sync service unavailable', true);
    } finally {
      if (document.contains(trigger)) {
        trigger.disabled = false;
        trigger.removeAttribute('aria-busy');
      }
    }
  };

  const addFromCartQuickView = async trigger => {
    const dialog = trigger.closest('[data-qx-cart-qv-dialog]');
    const id = Number($('[data-qx-cart-qv-variant]', dialog)?.value || 0);
    const quantity = clampQty($('[data-qx-cart-qv-qty]', dialog)?.value || 1);
    if (!id) {
      toast('Choose an available variant', true);
      return;
    }
    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');
    try {
      await addItems([{ id, quantity }], true);
      dialog?.close?.();
      openCart(trigger);
      toast('Added to cart');
    } catch (error) {
      toast(error.message || 'Could not add product', true);
      if (document.contains(trigger)) trigger.disabled = false;
    } finally {
      if (document.contains(trigger)) trigger.removeAttribute('aria-busy');
    }
  };

  const replaceCartVariant = async trigger => {
    const dialog = trigger.closest('[data-qx-cart-variant-dialog]');
    const id = Number($('[data-qx-cart-variant-id]', dialog)?.value || 0);
    const quantity = clampQty(dialog?.dataset.lineQuantity || 1);
    const oldLineKey = dialog?.dataset.lineKey || '';
    if (!id || !oldLineKey) {
      toast('Choose an available variant', true);
      return;
    }
    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');
    let added = false;
    try {
      await addItems([{ id, quantity }], true);
      added = true;
      await cartRequest('cart/change.js', { id: oldLineKey, quantity: 0 }, { open: true });
      dialog?.close?.();
      openCart(trigger);
      toast('Variant updated');
    } catch (error) {
      toast(added ? 'New variant was added, but the previous cart line could not be removed. Review your cart.' : (error.message || 'Could not update variant'), true);
    } finally {
      if (document.contains(trigger)) {
        trigger.disabled = false;
        trigger.removeAttribute('aria-busy');
      }
    }
  };

  const buyNowFromQuickView = async trigger => {
    const dialog = trigger.closest('[data-qv-dialog]');
    const id = Number($('[data-qv-variant-id]', dialog)?.value || 0);
    const quantity = clampQty($('[data-qv-qty-input]', dialog)?.value || 1);
    const status = $('[data-qv-status]', dialog);
    if (!id || trigger.disabled) return;
    trigger.disabled = true;
    trigger.classList.add('is-loading');
    trigger.setAttribute('aria-busy', 'true');
    if (dialog) dialog.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Preparing checkout…';
    try {
      await addItems([{ id, quantity }], false);
      location.href = ROOT + 'checkout';
    } catch (error) {
      trigger.disabled = false;
      trigger.classList.remove('is-loading');
      trigger.removeAttribute('aria-busy');
      dialog?.setAttribute('aria-busy', 'false');
      if (status) status.textContent = error.message || 'Unable to continue to checkout.';
      toast(error.message || 'Unable to continue to checkout', true);
    }
  };

  const saveWishlistVariant = trigger => {
    const dialog = trigger.closest('[data-qx-wishlist-variant-dialog]');
    const variantId = $('[data-qx-wishlist-variant-id]', dialog)?.value || '';
    const handle = dialog?.dataset.handle || '';
    if (!variantId || !handle) {
      toast('Choose an available variant', true);
      return;
    }
    const next = readWishlist().map(item => item.handle === handle ? {
      ...item,
      variantId: String(variantId),
      variantTitle: dialog.dataset.variantTitle || item.variantTitle || '',
      available: dialog.dataset.variantAvailable !== 'false',
      price: dialog.dataset.variantPrice || item.price
    } : item);
    writeWishlist(next);
    dialog?.close?.();
    syncWishlistUi();
    renderWishlistDrawer();
    renderWishlistPage();
    toast('Wishlist variant updated');
  };

  window.QXCommerce = {
    version: '1.2.0',
    cart: { request: cartRequest, addItems, open: openCart, close: closeCart, refresh: refreshCart, refreshCount: publishCart },
    wishlist: { read: readWishlist, write: writeWishlist, keyOf, render: () => { syncWishlistUi(); renderWishlistDrawer(); renderWishlistPage(); } }
  };
  document.documentElement.dataset.qxCommerceOwner = '1.2.0';
  document.documentElement.dataset.qxCartOwner = 'qx-commerce';
  document.documentElement.dataset.qxWishlistOwner = 'qx-commerce';

  document.addEventListener('click', async event => {
    const target = event.target;

    const syncWish = target.closest?.('[data-qx-wishlist-sync]');
    if (syncWish) { event.preventDefault(); event.stopImmediatePropagation(); await syncWishlistRemote(syncWish); return; }

    const cartQvAdd = target.closest?.('[data-qx-cart-qv-add]');
    if (cartQvAdd) { event.preventDefault(); event.stopImmediatePropagation(); await addFromCartQuickView(cartQvAdd); return; }

    const cartVariantSave = target.closest?.('[data-qx-cart-variant-save]');
    if (cartVariantSave) { event.preventDefault(); event.stopImmediatePropagation(); await replaceCartVariant(cartVariantSave); return; }

    const qvBuyNow = target.closest?.('[data-qv-buy-now]');
    if (qvBuyNow) { event.preventDefault(); event.stopImmediatePropagation(); await buyNowFromQuickView(qvBuyNow); return; }

    const wishlistVariantSave = target.closest?.('[data-qx-wishlist-variant-save]');
    if (wishlistVariantSave) { event.preventDefault(); event.stopImmediatePropagation(); saveWishlistVariant(wishlistVariantSave); return; }

    const wishlistToggle = target.closest?.('[data-qx-wishlist-toggle]');
    if (wishlistToggle) { event.preventDefault(); event.stopImmediatePropagation(); toggleWishlist(wishlistToggle); return; }

    const wishlistOpen = target.closest?.('[data-qx-wishlist-open]');
    if (wishlistOpen) { event.preventDefault(); event.stopImmediatePropagation(); openWishlist(wishlistOpen); return; }

    if (target.closest?.('[data-qx-wishlist-close]')) { event.preventDefault(); event.stopImmediatePropagation(); closeWishlist(); return; }

    const wishlistClear = target.closest?.('[data-qx-wishlist-drawer-clear],[data-qx-wishlist-clear]');
    if (wishlistClear) {
      event.preventDefault(); event.stopImmediatePropagation(); writeWishlist([]); syncWishlistUi(); renderWishlistDrawer(); renderWishlistPage(); toast('Wishlist cleared'); return;
    }

    const wishlistRemove = target.closest?.('[data-qx-commerce-wishlist-remove]');
    if (wishlistRemove) {
      event.preventDefault(); event.stopImmediatePropagation();
      const key = wishlistRemove.dataset.qxCommerceWishlistRemove;
      writeWishlist(readWishlist().filter(item => keyOf(item) !== key));
      syncWishlistUi(); renderWishlistDrawer(); renderWishlistPage(); toast('Removed from wishlist'); return;
    }

    const wishMinus = target.closest?.('[data-qx-commerce-wish-minus]');
    const wishPlus = target.closest?.('[data-qx-commerce-wish-plus]');
    if (wishMinus || wishPlus) {
      event.preventDefault(); event.stopImmediatePropagation();
      const input = $('[data-qx-commerce-wish-qty]', target.closest('[data-qx-wishlist-key]'));
      if (input) input.value = clampQty(Number(input.value || 1) + (wishPlus ? 1 : -1));
      return;
    }

    const wishlistMove = target.closest?.('[data-qx-commerce-wishlist-move]');
    if (wishlistMove) { event.preventDefault(); event.stopImmediatePropagation(); await moveWishlistItem(wishlistMove.dataset.qxCommerceWishlistMove, wishlistMove); return; }

    const saveLater = target.closest?.('[data-qx-cart-save-later]');
    if (saveLater) {
      event.preventDefault(); event.stopImmediatePropagation(); saveLater.disabled = true;
      const item = normalize({ handle: saveLater.dataset.handle, title: saveLater.dataset.title, url: saveLater.dataset.url, image: saveLater.dataset.image, price: saveLater.dataset.price, vendor: saveLater.dataset.vendor, variantId: saveLater.dataset.variantId, quantity: saveLater.dataset.lineQuantity, available: true });
      const before = readWishlist();
      const result = writeWishlist([item, ...before.filter(saved => keyOf(saved) !== keyOf(item))]);
      if (!result.persisted) { toast('Wishlist storage is unavailable; cart was not changed', true); saveLater.disabled = false; return; }
      try {
        await cartRequest('cart/change.js', { id: saveLater.dataset.lineKey, quantity: 0 }, { open: true });
        syncWishlistUi(); renderWishlistDrawer(); renderWishlistPage(); toast('Saved for later');
      } catch (error) {
        writeWishlist(before); toast(error.message || 'Could not save item for later', true);
      } finally {
        if (document.contains(saveLater)) saveLater.disabled = false;
      }
      return;
    }

    const cartChange = target.closest?.('[data-cart-change]');
    if (cartChange) {
      event.preventDefault(); event.stopImmediatePropagation(); cartChange.disabled = true;
      try {
        await cartRequest('cart/change.js', { line: Number(cartChange.dataset.line), quantity: Math.max(0, Number(cartChange.dataset.quantity || 0)) }, { open: true });
      } catch (error) {
        toast(error.message || 'Could not update cart', true);
        if (document.contains(cartChange)) cartChange.disabled = false;
      }
      return;
    }

    const quickAdd = target.closest?.('[data-cart-add]');
    if (quickAdd) {
      event.preventDefault(); event.stopImmediatePropagation(); quickAdd.disabled = true;
      try { await addItems([{ id: quickAdd.dataset.variantId, quantity: 1 }], true); openCart(quickAdd); }
      catch (error) { toast(error.message || 'Could not add product', true); if (document.contains(quickAdd)) quickAdd.disabled = false; }
      return;
    }

    const productFbt = target.closest?.('[data-qx-fbt-add]');
    if (productFbt) {
      event.preventDefault(); event.stopImmediatePropagation();
      const shell = productFbt.closest('[data-qx-fbt]');
      const status = $('[data-qx-fbt-status]', shell);
      const currentId = $('[data-product-root] [data-variant-id]')?.value || '';
      const ids = [currentId, ...$$('[data-qx-fbt-item]:checked', shell).map(input => input.value)].filter(Boolean);
      if (!ids.length) { if (status) status.textContent = 'Choose an available product.'; return; }
      productFbt.disabled = true;
      if (status) status.textContent = 'Adding…';
      try {
        await addItems(ids.map(id => ({ id, quantity: 1 })), true);
        if (status && document.contains(status)) status.textContent = 'Added to cart.';
        announce('Selected products added to cart'); openCart(productFbt);
      } catch (error) {
        if (status) status.textContent = error.message || 'Could not add selected products.';
        if (document.contains(productFbt)) productFbt.disabled = false;
      }
      return;
    }

    const cartFbt = target.closest?.('[data-fbt-add]');
    if (cartFbt) {
      event.preventDefault(); event.stopImmediatePropagation();
      const ids = $$('[data-fbt-variant]:checked', cartFbt.closest('[data-qx-cart-shell]')).map(input => input.value).filter(Boolean);
      if (!ids.length) return;
      cartFbt.disabled = true;
      try { await addItems(ids.map(id => ({ id, quantity: 1 })), true); }
      catch (error) { toast(error.message || 'Could not add selected items', true); if (document.contains(cartFbt)) cartFbt.disabled = false; }
      return;
    }

    const bundle = target.closest?.('[data-qx-cart-bundle-add],[data-qx-pdp-bundle-add]');
    if (bundle) {
      event.preventDefault(); event.stopImmediatePropagation();
      const wrap = bundle.closest('[data-qx-cart-bundle],[data-qx-pdp-bundle]');
      const items = $$('input[type="checkbox"]:checked', wrap).map(input => ({ id: input.value, quantity: 1 })).filter(item => item.id);
      if (!items.length) { toast('Select at least one product', true); return; }
      bundle.disabled = true;
      try { await addItems(items, true); toast('Bundle added'); }
      catch (error) { toast(error.message || 'Could not add bundle', true); if (document.contains(bundle)) bundle.disabled = false; }
      return;
    }

    const cartOpen = target.closest?.('[data-cart-open]');
    if (cartOpen) { event.preventDefault(); event.stopImmediatePropagation(); openCart(cartOpen); return; }
    if (target.closest?.('[data-cart-close]')) { event.preventDefault(); event.stopImmediatePropagation(); closeCart(); }
  }, true);

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add') || !$('[data-qx-cart-shell]')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const submit = event.submitter || $('[type="submit"]', form);
    if (submit) { submit.disabled = true; submit.setAttribute('aria-busy', 'true'); }
    try {
      await cartRequest('cart/add.js', new FormData(form), { open: true });
      form.closest('[data-qv-dialog]')?.close?.();
      openCart(submit);
    } catch (error) {
      toast(error.message || 'Unable to add this item to cart', true);
      if (submit && document.contains(submit)) { submit.disabled = false; submit.removeAttribute('aria-busy'); }
    }
  }, true);

  document.addEventListener('keydown', event => {
    const wishlist = $('[data-qx-wishlist-drawer]:not([hidden])');
    const cart = $('[data-qx-cart-shell].is-open');
    const active = wishlist ? $('.qx-wishlist-drawer__panel', wishlist) : cart ? $('.qx-cart-drawer', cart) : null;
    if (!active) return;
    if (event.key === 'Escape') {
      event.preventDefault(); event.stopImmediatePropagation(); wishlist ? closeWishlist() : closeCart(); return;
    }
    if (event.key !== 'Tab') return;
    const items = focusables(active);
    if (!items.length) return;
    const first = items[0];
    const last = items.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }, true);

  window.addEventListener('qx:wishlist-change', () => queueMicrotask(() => { syncWishlistUi(); renderWishlistDrawer(); renderWishlistPage(); }));
  window.addEventListener('storage', event => {
    if (event.key !== WKEY) return;
    syncWishlistUi(); renderWishlistDrawer(); renderWishlistPage();
  });

  const observeCommerceDom = () => {
    if (!document.body || document.body.dataset.qxCommerceObserved === 'true') return;
    document.body.dataset.qxCommerceObserved = 'true';
    new MutationObserver(mutations => {
      const cartAdded = mutations.some(mutation => [...mutation.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-qx-cart-shell],#shopify-section-qx-cart-drawer') || node.querySelector?.('[data-qx-cart-shell]'))));
      if (observerQueued || !cartAdded) return;
      observerQueued = true;
      queueMicrotask(() => { observerQueued = false; publishCart({ source: 'dom-observer' }); });
    }).observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    readWishlist(); syncWishlistUi(); renderWishlistDrawer(); renderWishlistPage(); publishCart({ source: 'init' }); observeCommerceDom();
    setTimeout(() => { syncWishlistUi(); renderWishlistDrawer(); renderWishlistPage(); }, 0);
  };

  document.addEventListener('shopify:section:load', () => setTimeout(init, 0));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
